import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useIsWebView, useToken, useMaster, MASTER_KEY, useDoctorRouteGuard } from '../../../shared/hooks';
import { useWebViewLoading } from '../../../shared/hooks/useWebViewLoading';
import { DoctorRouteDialog } from '../../../shared/components/DoctorRouteDialog';
import { appBridge } from '../../../shared/services/bridge';
import { RECEIPT_NOTICE_CONTACT_LINES } from '../constants/receiptTexts';
import { usePaymentInfo } from '../hooks/usePaymentInfo';
import { paymentRepository, isPaymentInfo } from '../data/paymentRepository';
import {
  type InvoiceBillingInput,
  INITIAL_BILLING_ADDRESS_FORM,
  isInvoiceBillingValid,
  buildInvoiceBillingAddress,
} from '../hooks/billingAddressUtil';
import { BillingAddressInput } from './BillingAddressInput';
import PaymentSummarySection from './PaymentSummarySection';
import ReceiptNotice from './ReceiptNotice';

import './PaymentConfirm.css';
import './PaymentCommon.css';


const CashPaymentConfirm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isWebView = useIsWebView();
  const token = useToken();
  const { getFormItems } = useMaster();

  const [isAgreed, setIsAgreed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [billingInput, setBillingInput] = useState<InvoiceBillingInput>({
    billingTypeId: '',
    billingName: '',
    addressForm: INITIAL_BILLING_ADDRESS_FORM,
  });

  // APIから決済情報を取得
  const { data: paymentData, isLoading, error } = usePaymentInfo(id || '', token);

  // ドクタールート監視ガード
  const { shouldBlock, handleConfirm: handleGuardConfirm } = useDoctorRouteGuard(id, token);

  // 支払方法が bill かどうか
  const isBillPayment = paymentData?.paymentMethodCode === 'bill';

  // billing_types / prefectures マスタ
  const billingTypeOptions = getFormItems(MASTER_KEY.BILLING_TYPES);
  const prefectureOptions = getFormItems(MASTER_KEY.PREFECTURES);

  // APIから既存の請求先情報を初期値として設定
  useEffect(() => {
    if (!paymentData || paymentData.paymentMethodCode !== 'bill') return;
    const billing = isPaymentInfo(paymentData) ? paymentData.billing : null;
    if (!billing) return;
    setBillingInput(prev => ({
      ...prev,
      billingTypeId: billing.billingTypeId || prev.billingTypeId,
      billingName: billing.name || prev.billingName,
      addressForm: {
        zipCode: billing.zipCode || prev.addressForm.zipCode,
        addressPrefectureId: billing.addressPrefectureId || prev.addressForm.addressPrefectureId,
        addressCity: billing.addressCity || prev.addressForm.addressCity,
        addressDetail: billing.addressDetail || prev.addressForm.addressDetail,
        addressBuilding: billing.addressBuilding || prev.addressForm.addressBuilding,
      },
    }));
  }, [paymentData]);

  // 金額情報を計算（APIから price=税抜金額、taxRate、commissionRate を取得）
  const price = paymentData?.price || 0;
  const taxRate = paymentData?.taxRate || 0;
  const commissionRate = paymentData?.commissionRate || 0;
  // 事務手数料加算後に消費税を計算（ドクター側calcHasTaxと同じロジック）
  const commissionAmount = Math.floor(price * commissionRate / 100);
  const subtotalWithCommission = price + commissionAmount;
  const tax = Math.floor(subtotalWithCommission * taxRate / 100);
  const total = subtotalWithCommission + tax;

  // Flutter側のローディング制御
  const contentRef = useWebViewLoading(isLoading);

  const handlePayment = async () => {
    if (!paymentData || !id) return;

    // bill 時は billingAddress バリデーション
    if (isBillPayment && !isInvoiceBillingValid(billingInput)) return;

    setIsSubmitting(true);
    appBridge.showLoading();

    const methodCode = paymentData.paymentMethodCode ?? 'cash';
    const billingAddress = isBillPayment
      ? buildInvoiceBillingAddress(billingInput)
      : undefined;

    try {
      const result = await paymentRepository.createPayment({
        inquiryId: id,
        paymentMethodCode: methodCode,
        price: paymentData.price,
        taxRate: paymentData.taxRate,
        ...(billingAddress ? { billingAddress } : {}),
      }, token);

      appBridge.hideLoading();
      if (result.success) {
        navigate(`/inquiries/${id}/payment-pending?method=${methodCode}`, { replace: true });
      } else {
        navigate(`/inquiries/${id}/payment-failed`);
      }
    } catch {
      appBridge.hideLoading();
      navigate(`/inquiries/${id}/payment-failed`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  // 支払方法を変更（支払方法選択画面へ遷移）
  const handleChangePaymentMethod = () => {
    if (!id) return;
    navigate(`/inquiries/${id}/payment-method?mode=change`);
  };

  // ローディング中はFlutter側のローディングが表示されるため、nullを返す
  if (isLoading) {
    return null;
  }

  // ドクタールートに切り替わっていた場合は Flutter ダッシュボードへ強制復帰
  if (shouldBlock) {
    return <DoctorRouteDialog onConfirm={handleGuardConfirm} />;
  }

  // エラー時
  if (error || !paymentData) {
    return (
      <div ref={contentRef} className="confirm-container">
        <main className="confirm-content">
          <p>決済情報の取得に失敗しました。</p>
        </main>
      </div>
    );
  }

  return (
    <div ref={contentRef} className="confirm-container">
      <main className="confirm-content">
        <h2 className="content-title">お支払い内容の確認</h2>

        {/* お支払い内容セクション（PPTX指示 0506 スライド12: 共通コンポーネント化） */}
        <PaymentSummarySection
          estimateGroups={paymentData?.estimateGroups}
          subtotalExcludingCommission={price}
          commissionAmount={commissionAmount}
          taxAmount={tax}
          totalAmount={total}
        />

        {/* 請求書払い時: 請求先情報入力 */}
        {isBillPayment && (
          <BillingAddressInput
            billingInput={billingInput}
            billingTypeOptions={billingTypeOptions}
            prefectureOptions={prefectureOptions}
            onChange={setBillingInput}
          />
        )}

        {/* 請求書払い時: 申込み案内文 */}
        {isBillPayment && (
          <p className="payment-instruction">
            金額をご確認の上、「請求書払いを申込む」を押してください。
          </p>
        )}

        {/* 領収書発行仕様変更（課題表381 / PDF P7）:
            請求書払いは HomeDoctor の領収書発行対象外のため、問い合わせ先を表示する。
            現金はカルテ側で領収書を発行するため表示しない。 */}
        {isBillPayment && <ReceiptNotice lines={RECEIPT_NOTICE_CONTACT_LINES} />}

        {/* 同意チェックボックス */}
        <div className="agreement-checkbox-container">
          <input
            type="checkbox"
            id="payment-agreement"
            checked={isAgreed}
            onChange={(e) => setIsAgreed(e.target.checked)}
          />
          <label htmlFor="payment-agreement">お支払い金額を確認しました</label>
        </div>
      </main>

      <footer className="confirm-footer">
        <button
          type="button"
          className="button-primary"
          onClick={handlePayment}
          disabled={!isAgreed || isSubmitting || (isBillPayment && !isInvoiceBillingValid(billingInput))}
        >
          {isBillPayment ? '請求書払いを申込む' : 'この内容で支払う'}
        </button>
        <button
          type="button"
          className="button-secondary"
          onClick={handleChangePaymentMethod}
          disabled={isSubmitting}
        >
          支払方法を変更
        </button>
        {!isWebView && (
          <button className="button-secondary" onClick={handleBack}>
            戻る
          </button>
        )}
      </footer>
    </div>
  );
};

export default CashPaymentConfirm;
